/**
 * Parse intel·ligent d'entrada de la llista de compra.
 * Detecta quantitats, unitats i extreu el nom base.
 *
 * Exemples:
 * - "ou" → { name: "ou", quantity: null, unit: null }
 * - "2 ous" → { name: "ous", quantity: 2, unit: "u" }
 * - "100g" → { name: "", quantity: 100, unit: "g" }
 * - "100g ou" → { name: "ou", quantity: 100, unit: "g" }
 * - "1kg manteiga" → { name: "manteiga", quantity: 1, unit: "kg" }
 * - "500ml llet" → { name: "llet", quantity: 500, unit: "ml" }
 */

export interface ParsedInput {
  name: string;
  quantity: number | null;
  unit: string | null;
}

// Unitats reconegudes, orderades per longitud descendent per evitar coincidències parcials.
// Incloent variants en català, castellà i anglès.
const UNIT_PATTERNS = [
  // Mass
  "quilograms?",
  "kilogramos?",
  "kilos?",
  "quilos?",
  "gramos?",
  "grams?",
  "grames?",
  "mg",
  "milligrams?",

  // Volume
  "litres?",
  "liters?",
  "litros?",
  "millilitres?",
  "milliliters?",
  "mililitros?",
  "centilitres?",
  "centiliters?",
  "centilitros?",
  "decilitres?",
  "deciliters?",
  "decilitros?",

  // Count
  "unitats?",
  "unidades?",
  "units?",
  "u\\.?d\\.?s\\.?",
  "uds\\.",

  // Spoons
  "tablespoons?",
  "cucharadas?",
  "cucharitas?",
  "teaspoons?",

  // Abbrevs
  "kg",
  "g",
  "l",
  "ml",
  "cl",
  "dl",
  "u",
  "ud",
  "uds",
  "tbsp",
  "tsp",

  // Other (no metric)
  "pessic",
  "pessics",
  "pessiques",
  "pam",
  "pams",
  "gust",
  "gusts",
];

// Construeix un regex que detecta una unitat seguida opcionalment d'un punt.
// S'ordenen per longitud descendent per evitar overlap (ej: "kg" matching en "kilogram").
const UNIT_PATTERN_SORTED = [...UNIT_PATTERNS].sort((a, b) => b.length - a.length);
const UNIT_REGEX = new RegExp(`^(${UNIT_PATTERN_SORTED.join("|")})\\.?$`, "i");

/**
 * Parse d'una cadena com "2 ous", "100g ou", etc.
 * Retorna { name, quantity, unit } extrets intel·ligentment.
 */
export function parseShoppingInput(input: string): ParsedInput {
  const trimmed = input.trim();
  if (!trimmed) return { name: "", quantity: null, unit: null };

  let quantity: number | null = null;
  let unit: string | null = null;
  let remaining = trimmed;

  // 1. Detecta quantitat al principi (números, incloent decimals).
  const numMatch = remaining.match(/^(\d+(?:\.\d+)?)\s*/);
  if (numMatch) {
    quantity = parseFloat(numMatch[1]);
    remaining = remaining.slice(numMatch[0].length);
  }

  // 2. Si hem detectat quantitat, busca unitat al principi de remaining.
  // Si no hem detectat quantitat, busca unitat al principi del trimmed original.
  const searchStr = quantity !== null ? remaining : trimmed;
  const tokens = searchStr.split(/\s+/);

  if (tokens.length > 0 && UNIT_REGEX.test(tokens[0])) {
    unit = normalizeUnit(tokens[0]);
    // Elimina la unitat del remaining/trimmed
    if (quantity !== null) {
      remaining = remaining.slice(tokens[0].length).trim();
    } else {
      remaining = searchStr.slice(tokens[0].length).trim();
    }
  }

  // 3. El que queda és el nom.
  const name = remaining.toLowerCase();

  // 4. Si hi ha quantitat però no unitat i hi ha nom, assumim unitats (u).
  if (quantity !== null && unit === null && name) {
    unit = "u";
  }

  // 5. Si no hi ha quantitat ni unitat però hi ha nom, és 1 unitat implícita.
  if (quantity === null && unit === null && name) {
    quantity = 1;
    unit = "u";
  }

  return {
    name,
    quantity: quantity !== null && unit !== null ? quantity : null,
    unit,
  };
}

/**
 * Normalitza una unitat detectada a una forma estàndard.
 * Ej: "quilograms" → "kg", "grams" → "g", "ous" → "u", etc.
 */
export function normalizeUnit(rawUnit: string): string {
  const u = rawUnit.trim().toLowerCase().replace(/\.$/, "");

  // Mass
  if (/^(kilograms?|kilogramos?|kilos?|quilos?)$/.test(u)) return "kg";
  if (/^(gramos?|grams?|grames?)$/.test(u)) return "g";
  if (/^mg$/.test(u)) return "mg";

  // Volume
  if (/^(litres?|liters?|litros?)$/.test(u)) return "l";
  if (/^(millilitres?|milliliters?|mililitros?)$/.test(u)) return "ml";
  if (/^(centilitres?|centiliters?|centilitros?)$/.test(u)) return "cl";
  if (/^(decilitres?|deciliters?|decilitros?)$/.test(u)) return "dl";

  // Count
  if (/^(unitats?|unidades?|units?|u\.?d\.?s?\.?)$/.test(u)) return "u";

  // Spoons
  if (/^(tablespoons?|cucharadas?|tbsp)$/.test(u)) return "tbsp";
  if (/^(teaspoons?|cucharitas?|tsp)$/.test(u)) return "tsp";

  // Non-metric (es mantenen com están)
  if (/^pessic/.test(u)) return "pessic";
  if (/^pam/.test(u)) return "pam";
  if (/^gust/.test(u)) return "gust";

  // Si no es reconeix, es retorna com està (però normalitzat a minúscules)
  return u;
}
